import React, { useEffect, useRef, useState, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import './DerivedDataChart.css';

const MINUTE_INTERVAL = 60;

const alignToMinute = (timestamp) => Math.floor(timestamp / MINUTE_INTERVAL) * MINUTE_INTERVAL;

const getMarketHours = () => {
    const now = new Date();
    const start = new Date(now);
    start.setHours(9, 15, 0, 0);
    const end = new Date(now);
    end.setHours(15, 30, 0, 0);
    return {
        start: Math.floor(start.getTime() / 1000),
        end: Math.floor(end.getTime() / 1000),
    };
};

const isWithinMarketHours = (timestamp) => {
    const { start, end } = getMarketHours();
    return timestamp >= start && timestamp <= end;
};

const formatTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
    });
};

const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
    }) + ' ' + now.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone: 'Asia/Kolkata'
    });
};

const DerivedDataChart = ({ indexName, realtimeCandle, historicalData, timeWindowMinutes = 0 }) => {
    const chartRef = useRef(null);
    // Use state to store chart data - this ensures React properly tracks changes
    const [chartData, setChartData] = useState({
        times: [],
        spot: [],
        future: [],
        straddle: [],
        atm: [],
    });
    // Use ref to store the Maps for efficient updates
    const dataMapRef = useRef({
        spot: new Map(),
        future: new Map(),
        straddle: new Map(),
        atm: new Map(),
    });

    // Initialize with historical data when it changes
    useEffect(() => {
        if (!historicalData || historicalData.length === 0) return;

        const now = Math.floor(Date.now() / 1000);
        const windowStart = timeWindowMinutes > 0 ? now - (timeWindowMinutes * 60) : 0;

        // Reset maps
        dataMapRef.current = {
            spot: new Map(),
            future: new Map(),
            straddle: new Map(),
            atm: new Map(),
        };

        const sortedData = [...historicalData].sort((a, b) => a.timestamp - b.timestamp);

        sortedData.forEach(point => {
            const time = point.timestamp;
            if (timeWindowMinutes > 0 && time < windowStart) return;
            if (!isWithinMarketHours(time)) return;

            const minuteTime = alignToMinute(time);

            dataMapRef.current.spot.set(minuteTime, point.index_price);
            dataMapRef.current.future.set(minuteTime, point.synthetic_future);
            dataMapRef.current.straddle.set(minuteTime, point.straddle_price);
            dataMapRef.current.atm.set(minuteTime, point.atm_strike);
        });

        // Update state with the processed data
        updateChartData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historicalData, timeWindowMinutes]); // updateChartData is defined after, uses refs

    // Reset when index changes
    useEffect(() => {
        dataMapRef.current = {
            spot: new Map(),
            future: new Map(),
            straddle: new Map(),
            atm: new Map(),
        };
        setChartData({
            times: [],
            spot: [],
            future: [],
            straddle: [],
            atm: [],
        });
    }, [indexName]);

    // Handle real-time updates
    useEffect(() => {
        if (!realtimeCandle) return;

        const { timestamp, spot_price, synthetic_future, straddle_price, atm_strike } = realtimeCandle;
        if (!timestamp || !spot_price) return;
        if (!isWithinMarketHours(timestamp)) return;

        const minuteTime = alignToMinute(timestamp);

        // Update maps with new data
        dataMapRef.current.spot.set(minuteTime, spot_price);
        dataMapRef.current.future.set(minuteTime, synthetic_future);
        dataMapRef.current.straddle.set(minuteTime, straddle_price);
        if (atm_strike) dataMapRef.current.atm.set(minuteTime, atm_strike);

        // Update state to trigger re-render
        updateChartData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [realtimeCandle]); // updateChartData is defined after, uses refs

    // Helper to convert maps to arrays and update state
    const updateChartData = useCallback(() => {
        const times = [...new Set([
            ...dataMapRef.current.spot.keys(),
            ...dataMapRef.current.future.keys(),
            ...dataMapRef.current.straddle.keys(),
            ...dataMapRef.current.atm.keys(),
        ])].sort((a, b) => a - b);

        setChartData({
            times,
            spot: times.map(t => dataMapRef.current.spot.get(t) || null),
            future: times.map(t => dataMapRef.current.future.get(t) || null),
            straddle: times.map(t => dataMapRef.current.straddle.get(t) || null),
            atm: times.map(t => dataMapRef.current.atm.get(t) || null),
        });
    }, []);

    const getChartOption = useCallback(() => {
        const { times, spot, future, straddle, atm } = chartData;

        // Calculate dynamic offsets based on last values
        const lastSpot = spot.filter(v => v !== null).slice(-1)[0] || 0;
        const lastFuture = future.filter(v => v !== null).slice(-1)[0] || 0;
        const lastAtm = atm.filter(v => v !== null).slice(-1)[0] || 0;

        // Sort to determine rank (highest to lowest)
        const values = [
            { name: 'spot', value: lastSpot },
            { name: 'future', value: lastFuture },
            { name: 'atm', value: lastAtm },
        ].sort((a, b) => b.value - a.value);

        // Assign offsets: highest=-24 (up), middle=0, lowest=+24 (down)
        const offsets = {};
        values.forEach((item, index) => {
            offsets[item.name] = index === 0 ? -24 : index === 1 ? 0 : 24;
        });

        return {
            animation: false,
            title: {
                text: 'ATM Premiums',
                subtext: formatDateTime(),
                left: 'center',
                top: 5,
                textStyle: { fontSize: 14, fontWeight: 500 },
                subtextStyle: { fontSize: 11, color: '#666' },
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'cross', animation: false },
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderColor: '#ddd',
                borderWidth: 1,
                textStyle: { fontSize: 11 },
                formatter: (params) => {
                    if (!params || params.length === 0) return '';
                    let html = `<strong>${params[0].axisValue}</strong><br/>`;
                    params.forEach(p => {
                        if (p.value !== null && p.value !== undefined) {
                            html += `<span style="color:${p.color}">${p.seriesName}</span>: ${p.value.toFixed(2)}<br/>`;
                        }
                    });
                    return html;
                },
            },
            legend: {
                data: ['Net premium', 'Spot price', 'Fair price', 'ATM strike'],
                bottom: 5,
                textStyle: { fontSize: 10 },
                icon: 'line',
                itemWidth: 20,
                itemHeight: 2,
            },
            grid: {
                left: 55,
                right: 55,
                top: 55,
                bottom: 75,
            },
            xAxis: {
                type: 'category',
                data: times.map(formatTime),
                axisLabel: { fontSize: 9, color: '#666' },
                axisLine: { lineStyle: { color: '#ddd' } },
                axisTick: { lineStyle: { color: '#ddd' } },
                boundaryGap: false,
            },
            yAxis: [
                {
                    type: 'value',
                    name: 'Spot price',
                    nameTextStyle: { fontSize: 9, color: '#22C55E' },
                    position: 'left',
                    axisLabel: { fontSize: 9, color: '#666' },
                    axisLine: { show: true, lineStyle: { color: '#ddd' } },
                    splitLine: { lineStyle: { color: '#f0f0f0' } },
                    scale: true,
                    min: 'dataMin',
                    max: 'dataMax',
                },
                {
                    type: 'value',
                    name: 'Net premium',
                    nameTextStyle: { fontSize: 9, color: '#7C3AED' },
                    position: 'right',
                    axisLabel: { fontSize: 9, color: '#666' },
                    axisLine: { show: true, lineStyle: { color: '#ddd' } },
                    splitLine: { show: false },
                    scale: true,
                    min: 'dataMin',
                    max: 'dataMax',
                },
            ],
            dataZoom: [
                {
                    type: 'slider',
                    show: true,
                    xAxisIndex: [0],
                    bottom: 28,
                    height: 18,
                    borderColor: '#ddd',
                    fillerColor: 'rgba(59,130,246,0.1)',
                    handleStyle: { color: '#3B82F6' },
                    textStyle: { fontSize: 9 },
                },
                {
                    type: 'inside',
                    xAxisIndex: [0],
                },
            ],
            series: [
                {
                    name: 'Net premium',
                    type: 'line',
                    data: straddle,
                    yAxisIndex: 1,
                    symbol: 'none',
                    lineStyle: { width: 2, color: '#7C3AED' },
                    itemStyle: { color: '#7C3AED' },
                    endLabel: {
                        show: true,
                        formatter: (params) => params.value?.toFixed(2) || '',
                        color: '#fff',
                        backgroundColor: '#7C3AED',
                        padding: [2, 4],
                        borderRadius: 2,
                        fontSize: 9,
                    },
                },
                {
                    name: 'Spot price',
                    type: 'line',
                    data: spot,
                    yAxisIndex: 0,
                    symbol: 'none',
                    lineStyle: { width: 1.5, color: '#22C55E', type: 'dashed' },
                    itemStyle: { color: '#22C55E' },
                    endLabel: {
                        show: true,
                        formatter: (params) => params.value?.toFixed(2) || '',
                        color: '#fff',
                        backgroundColor: '#22C55E',
                        padding: [2, 4],
                        borderRadius: 2,
                        fontSize: 9,
                        offset: [0, offsets.spot],
                    },
                },
                {
                    name: 'Fair price',
                    type: 'line',
                    data: future,
                    yAxisIndex: 0,
                    symbol: 'none',
                    lineStyle: { width: 1.5, color: '#EC4899', type: 'dashed' },
                    itemStyle: { color: '#EC4899' },
                    endLabel: {
                        show: true,
                        formatter: (params) => params.value?.toFixed(2) || '',
                        color: '#fff',
                        backgroundColor: '#EC4899',
                        padding: [2, 4],
                        borderRadius: 2,
                        fontSize: 9,
                        offset: [0, offsets.future],
                    },
                },
                {
                    name: 'ATM strike',
                    type: 'line',
                    data: atm,
                    yAxisIndex: 0,
                    symbol: 'none',
                    step: 'middle',
                    lineStyle: { width: 1.5, color: '#F97316', type: 'dotted' },
                    itemStyle: { color: '#F97316' },
                    endLabel: {
                        show: true,
                        formatter: (params) => params.value || '',
                        color: '#fff',
                        backgroundColor: '#F97316',
                        padding: [2, 4],
                        borderRadius: 2,
                        fontSize: 9,
                        offset: [0, offsets.atm],
                    },
                },
            ],
        };
    }, [chartData]);

    return (
        <div className="derived-chart-container">
            <ReactECharts
                ref={chartRef}
                option={getChartOption()}
                style={{ height: '100%', width: '100%' }}
                notMerge={false}
                lazyUpdate={true}
            />
        </div>
    );
};

export default DerivedDataChart;
